import { useState, useEffect, useRef, useCallback } from 'react'
import ChartMenu, { MenuRow, MenuSelect, MenuSwatch, MenuWeight, MenuText, MenuDivider, MenuActions, MenuButton, MenuIndex } from './menu/ChartMenu'
import { dbg } from '../utils/debugFlag'

// ═══════════════════════════════════════════════════════════════════════
// TRENDLINES
// Lives here rather than in FootprintChartCanvas because a trendline IS an
// S/R line with two anchors instead of one — same color/weight/style/label
// contract, same settings toolbar (the component below), same interaction
// grammar. Keeping them together means the chart file holds one hook call
// and one draw call instead of ~200 lines of drawing state.
//
// Anchors are stored in DATA space (startTime, mcap), never pixels. Pixel
// anchors drift the instant anything pans, zooms or rebins; data anchors
// stay welded to the bars they were drawn against.
// ═══════════════════════════════════════════════════════════════════════

// Visual size and hit size are SEPARATE on purpose: a grab target wants to
// be forgiving, a chart marking wants to be small. Big squares are only
// necessary if the two are conflated.
// ═══════════════════════════════════════════════════════════════════════
// PER-TOKEN DRAWING STORE (bounded)
// ═══════════════════════════════════════════════════════════════════════
// Two problems this solves.
//
// SCOPING: drawings belong to the instrument they were drawn on. A trendline
// off ETH's low means nothing on a memecoin. Trendlines were keyed on
// chart-slot + mode, so they followed you from token to token.
//
// GROWTH: one localStorage key per token, written forever, is a slow leak —
// visit a few hundred memecoins and the quota (~5MB) fills, at which point
// EVERY setItem in the app starts throwing and unrelated settings silently
// stop saving. So the store is bounded: an LRU index caps how many tokens
// keep drawings, empty sets DELETE their key instead of writing "[]", and
// evicting a token removes every drawing kind it owned.
const DRAW_PREFIX = 'cheddar_draw'
const DRAW_INDEX = 'cheddar_draw_index'   // [token, ...] most-recent-first
const DRAW_MAX_TOKENS = 40                // tokens that keep drawings
const DRAW_KINDS = ['trend', 'sr']

const drawKey = (kind, token) => `${DRAW_PREFIX}_${kind}_${token}`

function readIndex() {
  try { const v = JSON.parse(localStorage.getItem(DRAW_INDEX) || '[]'); return Array.isArray(v) ? v : [] } catch { return [] }
}

/** Mark a token as most-recently-used and evict anything past the cap. */
function touchIndex(token) {
  if (!token) return
  let idx = readIndex().filter(t => t !== token)
  idx.unshift(token)
  const evicted = idx.slice(DRAW_MAX_TOKENS)
  idx = idx.slice(0, DRAW_MAX_TOKENS)
  try {
    for (const t of evicted) for (const k of DRAW_KINDS) localStorage.removeItem(drawKey(k, t))
    localStorage.setItem(DRAW_INDEX, JSON.stringify(idx))
  } catch {}
}

export function loadDrawings(kind, token) {
  if (!token) return []
  try {
    const raw = localStorage.getItem(drawKey(kind, token))
    if (raw == null) return []
    const v = JSON.parse(raw)
    return Array.isArray(v) ? v : []
  } catch { return [] }
}

export function saveDrawings(kind, token, items) {
  if (!token) return
  try {
    if (!items || items.length === 0) {
      // Nothing to keep: remove the key rather than store an empty array.
      // Most tokens you draw on end up cleared, and "[]" forever is the
      // bulk of the bloat.
      localStorage.removeItem(drawKey(kind, token))
      const stillHas = DRAW_KINDS.some(k => localStorage.getItem(drawKey(k, token)) != null)
      if (!stillHas) {
        try { localStorage.setItem(DRAW_INDEX, JSON.stringify(readIndex().filter(t => t !== token))) } catch {}
      }
      return
    }
    localStorage.setItem(drawKey(kind, token), JSON.stringify(items))
    touchIndex(token)
  } catch {}
}

/**
 * One-time sweep of the LEGACY per-token S/R keys (`solcheddar_srlines_*`),
 * which were written unbounded and never cleaned. Drops empty ones outright
 * and keeps only the newest DRAW_MAX_TOKENS of the rest. Safe to call on
 * every mount — it is a no-op once under the cap.
 */
export function pruneLegacyDrawings() {
  try {
    const keys = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith('solcheddar_srlines_')) keys.push(k)
    }
    let removed = 0
    const kept = []
    for (const k of keys) {
      const v = localStorage.getItem(k)
      if (!v || v === '[]' || v === 'null') { localStorage.removeItem(k); removed++ }
      else kept.push(k)
    }
    if (kept.length > DRAW_MAX_TOKENS) {
      // No timestamps on the legacy keys, so drop the tail of enumeration
      // order. Losing a drawing on a token you have not opened in months is
      // a far smaller cost than a full quota.
      for (const k of kept.slice(DRAW_MAX_TOKENS)) { localStorage.removeItem(k); removed++ }
    }
    if (removed) dbg(`[Drawings] pruned ${removed} stale key(s)`)
  } catch {}
}

export const PIP = 2.5        // half-size of the desktop trendline square (5px)
// Phone handles are this square, doubled: 10x10. Same size on AVWAP,
// trendline, and S/R. Hit radius stays larger than the drawn square.
export const PHONE_HANDLE_HALF = PIP * 2
export const PHONE_HANDLE_HIT = 16
export function clampHandleX(sx, plotWidth, half = PHONE_HANDLE_HALF) {
  const w = Math.max(half * 2, plotWidth || 0)
  if (!(sx > half)) return half
  return Math.min(w - half, sx)
}
const GRAB_PX = 10     // endpoint grab radius (invisible, generous)
const HOVER_PX = 9     // line hover tolerance
const HOVER_TOUCH_PX = 22 // finger-sized line select / menu hit
const PHONE_LONG_MS = 450
const PHONE_SLOP = 10

/** Distance from a point to a line SEGMENT (not the infinite line). */
/** Distance from (px,py) to the BORDER of an axis-aligned rect given by two corners. */
export function distToRectBorder(px, py, x1, y1, x2, y2) {
  return Math.min(
    distToSegment(px, py, x1, y1, x2, y1), distToSegment(px, py, x2, y1, x2, y2),
    distToSegment(px, py, x2, y2, x1, y2), distToSegment(px, py, x1, y2, x1, y1))
}

export function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(px - x1, py - y1)
  let t = ((px - x1) * dx + (py - y1) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
}

/** Distance from a point to a quadratic Bezier, via ~samples polyline segments. */
function distToQuad(px, py, x0, y0, cx, cy, x1, y1, samples = 24) {
  let best = Infinity
  let ax = x0, ay = y0
  for (let i = 1; i <= samples; i++) {
    const t = i / samples
    const u = 1 - t
    const bx = u * u * x0 + 2 * u * t * cx + t * t * x1
    const by = u * u * y0 + 2 * u * t * cy + t * t * y1
    const d = distToSegment(px, py, ax, ay, bx, by)
    if (d < best) best = d
    ax = bx; ay = by
  }
  return best
}

/** On-curve mid → quadratic CONTROL point so the curve passes through
 *  (mx,my) at t=0.5. Stored mid stays ON the stroke; only draw uses C. */
function quadControlFromMid(x0, y0, mx, my, x1, y1) {
  return {
    cx: 2 * mx - 0.5 * x0 - 0.5 * x1,
    cy: 2 * my - 0.5 * y0 - 0.5 * y1,
  }
}

/** Arc as a BEND: -1..1, the on-curve mid's offset perpendicular to the
 *  chord, in half-chord lengths (1 = mid sits half the chord's length off
 *  it). Screen-space, so it reads the same at any zoom. Stored on the line
 *  as `bend` and persisted with it; legacy lines may still carry `mid`. */
export function midFromBend(x1, y1, x2, y2, bend, along = 0) {
  const dx = x2 - x1, dy = y2 - y1
  const half = Math.hypot(dx, dy) / 2
  const len = half * 2 || 1
  // Unit normal (-dy, dx)/len; positive bend bows to the left of a1->a2.
  // `along` (srline-trend-20261019) slides the node along the chord, in
  // half-chord lengths from the middle (0 = middle, legacy lines).
  const a = Number.isFinite(along) ? along : 0
  return {
    x: (x1 + x2) / 2 + (-dy / len) * bend * half + (dx / len) * a * half,
    y: (y1 + y2) / 2 + (dx / len) * bend * half + (dy / len) * a * half,
  }
}
/** Node position ALONG the chord, -0.9..0.9 half-chords from the middle.
 *  Clamped short of the ends so the curve can't fold back on itself. */
export function alongFromPoint(x1, y1, x2, y2, px, py) {
  const dx = x2 - x1, dy = y2 - y1
  const len = Math.hypot(dx, dy)
  if (!(len > 0)) return 0
  const off = ((px - (x1 + x2) / 2) * dx + (py - (y1 + y2) / 2) * dy) / len
  return Math.max(-0.9, Math.min(0.9, off / (len / 2)))
}
export function bendFromPoint(x1, y1, x2, y2, px, py) {
  const dx = x2 - x1, dy = y2 - y1
  const len = Math.hypot(dx, dy)
  if (!(len > 0)) return 0
  const off = ((px - (x1 + x2) / 2) * (-dy) + (py - (y1 + y2) / 2) * dx) / len
  return Math.max(-1, Math.min(1, off / (len / 2)))
}

/** Distance to a bent segment whose mid is ON-CURVE (not the control). */
function distToBent(px, py, x0, y0, mx, my, x1, y1) {
  const { cx, cy } = quadControlFromMid(x0, y0, mx, my, x1, y1)
  return distToQuad(px, py, x0, y0, cx, cy, x1, y1)
}

/** Ray flag: dedicated `ray` boolean, or legacy style === 'ray'. */
function isRayLine(line) {
  return !!(line && (line.ray || line.style === 'ray'))
}

/** Stroke pattern only — legacy style 'ray' draws as solid + ray flag. */
function dashKind(style) {
  if (style === 'dashed') return 'dashed'
  if (style === 'dotted') return 'dotted'
  return 'solid'
}

/** Shoot from (x,y) along (dx,dy) to the plot edge (0..plotW × 0..plotH). */
function rayOutToEdge(x, y, dx, dy, plotW, plotH) {
  if (dx === 0 && dy === 0) return { x: plotW, y }
  let t = Infinity
  if (dx > 0) t = Math.min(t, (plotW - x) / dx)
  if (dx < 0) t = Math.min(t, (0 - x) / dx)
  if (dy > 0) t = Math.min(t, (plotH - y) / dy)
  if (dy < 0) t = Math.min(t, (0 - y) / dy)
  if (!Number.isFinite(t) || t < 0) {
    // Fallback: run right across the plot (classic chart ray).
    const s = dx !== 0 ? dy / dx : 0
    return { x: plotW, y: y + s * (plotW - x) }
  }
  return { x: x + dx * t, y: y + dy * t }
}




/**
 * Distance from a point to a RAY: anchored at (x1,y1), through (x2,y2), and
 * running on forever. Same as the segment version but with no upper clamp on
 * t — the far end is unbounded.
 *
 * A ray DRAWS from its anchor out to the plot edge, but hit-testing used the
 * segment distance, so everything past (x2,y2) was visible and un-grabbable:
 * you could see the line but not hover or select it. The drawn extent and the
 * hit extent have to be the same thing or the line lies about where it is.
 */
/**
 * A ray's ANCHOR and its direction point, normalised so the anchor is always
 * the LEFT end and the ray runs rightward.
 *
 * The draw pass used to moveTo(x1,y1) unconditionally, so which end anchored
 * depended on the direction you happened to DRAG. Draw right-to-left and x1
 * is the RIGHT point: the ray began mid-chart, shot right, and left the x2
 * pip stranded on the left with no line reaching it — "ray doesn't extend
 * right." A ray extends right from its start, so the start is whichever end
 * is further left, not whichever end got clicked first. Draw and hit-test
 * both come through here so they can never disagree about where it is.
 */
export function rayEnds(x1, y1, x2, y2) {
  // Anchor = the FIRST CLICK, always. The previous version anchored at the
  // leftmost point, which silently moved the anchor to the SECOND click
  // whenever it landed left of the first — not the spec. The spec: the ray
  // starts at the click point and extends RIGHT. If the direction handle is
  // left of the anchor, mirror it through the anchor: same slope, rightward.
  if (x2 >= x1) return { ax: x1, ay: y1, bx: x2, by: y2 }
  return { ax: x1, ay: y1, bx: 2 * x1 - x2, by: 2 * y1 - y2 }
}

export function distToRay(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(px - x1, py - y1)
  const t = Math.max(0, ((px - x1) * dx + (py - y1) * dy) / len2)
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
}

/**
 * Pixel x → candle index, and the inverse. These are each other's exact
 * inverse over the visible range; the draw pass and the click handler both
 * go through them so a click can never land on a different bar than the
 * one the cursor was touching.
 */
export function xOfIndex(idxF, g) {
  const fromR = g.visibleCount - 1 - (idxF - g.startIdx)
  return g.plotWidth - (fromR + 1) * g.cw + g.panOffsetPx + g.cw / 2
}
// FRACTIONAL — no rounding. Snapping the index to whole candles is what made
// dragged endpoints jump a column at a time instead of tracking the hand,
// and what made the last candle a wall you couldn't draw past. The y axis
// was continuous all along (mcapToY works in fractional rows); only time
// was quantised.
export function indexOfX(x, g) {
  const fromR = (g.plotWidth + g.panOffsetPx - x) / g.cw - 0.5
  return g.startIdx + (g.visibleCount - 1 - fromR)
}

/**
 * Fractional index → timestamp, and back. Interpolates BETWEEN candles and
 * extrapolates BEYOND them on the timeframe interval, so an anchor can sit
 * anywhere — including out in the future padding where no candle exists yet.
 * When that slot eventually fills, the anchor is already at the right time
 * and simply lands on the new bar.
 */
export function timeOfIndexF(idxF, candles, intervalMs) {
  const n = candles?.length || 0
  if (!n || !(intervalMs > 0)) return 0
  if (idxF >= n - 1) return candles[n - 1].startTime + (idxF - (n - 1)) * intervalMs
  if (idxF <= 0) return candles[0].startTime + idxF * intervalMs
  const i = Math.floor(idxF)
  const a = candles[i].startTime, b = candles[i + 1].startTime
  return a + (idxF - i) * (b - a)
}
export function indexFOfTime(t, candles, intervalMs) {
  const n = candles?.length || 0
  if (!n || !(intervalMs > 0)) return 0
  if (t >= candles[n - 1].startTime) return (n - 1) + (t - candles[n - 1].startTime) / intervalMs
  if (t <= candles[0].startTime) return (t - candles[0].startTime) / intervalMs
  let lo = 0, hi = n - 1, idx = 0
  while (lo <= hi) {
    const m = (lo + hi) >> 1
    if (candles[m].startTime <= t) { idx = m; lo = m + 1 } else { hi = m - 1 }
  }
  const a = candles[idx].startTime, b = candles[Math.min(idx + 1, n - 1)].startTime
  return idx + (b > a ? (t - a) / (b - a) : 0)
}

/** Nearest candle index at or before a timestamp. */
export function indexOfTime(t, candles) {
  if (!candles || candles.length === 0) return -1
  let lo = 0, hi = candles.length - 1, idx = 0
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    // <= NOT <: anchors store a candle's startTime EXACTLY, so a strict
    // less-than fails on the exact match and selects the PREVIOUS bar —
    // which is why every placed point landed one column to the left.
    if (candles[mid].startTime <= t) { idx = mid; lo = mid + 1 } else { hi = mid - 1 }
  }
  return idx
}

/**
 * Paint every trendline plus the in-progress rubber band.
 * Returns the screen segments so hover/drag hit-testing measures against
 * exactly what was painted — the highlight can never disagree with the eye.
 */
export function drawTrendlines(ctxMain, {
  lines = [], pending = null, cursor = null, hoveredId = null, dragging = null,
  candles, geom, mcapToY, phone = false,
  layerCtx = null,   // zindex-20261102: (index) => ctx for that layer
}) {
  let ctx = ctxMain
  const segs = []
  if (!candles || !candles.length || !geom) return segs
  const xOfTime = (t) => xOfIndex(indexFOfTime(t, candles, geom.intervalMs), geom)

  // Rubber band: start anchor placed, end follows the cursor until the
  // second click lands. Drawn in the style the finished line will take.
  if (pending && cursor && pending.shape === 'rect') {
    // Rectangle rubber band: corner 1 fixed, corner 2 follows the cursor.
    const bx = xOfTime(pending.t), by = mcapToY(pending.mcap)
    ctx.save()
    ctx.fillStyle = 'rgba(79,195,247,0.12)'
    ctx.fillRect(Math.min(bx, cursor.x), Math.min(by, cursor.y), Math.abs(cursor.x - bx), Math.abs(cursor.y - by))
    ctx.strokeStyle = '#4fc3f7'
    ctx.lineWidth = 1
    ctx.setLineDash([5, 4])
    ctx.strokeRect(Math.min(bx, cursor.x), Math.min(by, cursor.y), Math.abs(cursor.x - bx), Math.abs(cursor.y - by))
    ctx.setLineDash([])
    ctx.restore()
  } else if (pending && cursor) {
    const bx = xOfTime(pending.t), by = mcapToY(pending.mcap)
    ctx.save()
    ctx.strokeStyle = '#ffaa00'
    ctx.lineWidth = 1
    ctx.setLineDash([5, 4])
    ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(cursor.x, cursor.y); ctx.stroke()
    ctx.setLineDash([])
    ctx.fillStyle = '#ffaa00'
    const band = phone ? PHONE_HANDLE_HALF : PIP
    ctx.fillRect(bx - band, by - band, band * 2, band * 2)
    ctx.restore()
  }

  for (const tl of lines) {
    ctx = layerCtx ? layerCtx(tl.index) : ctxMain   // zindex-20261102
    if (tl.shape === 'rect') {
      // ─── RECTANGLE (rect-draw-20261016) ───
      // Corners a=(t1,m1) b=(t2,m2) c=(t1,m2) d=(t2,m1); 'body' moves both.
      let t1 = tl.t1, m1 = tl.m1, t2 = tl.t2, m2 = tl.m2
      if (dragging && dragging.id === tl.id && cursor && cursor.t != null) {
        const e = dragging.end
        if (e === 'a') { t1 = cursor.t; m1 = cursor.mcap }
        else if (e === 'b') { t2 = cursor.t; m2 = cursor.mcap }
        else if (e === 'c') { t1 = cursor.t; m2 = cursor.mcap }
        else if (e === 'd') { t2 = cursor.t; m1 = cursor.mcap }
        else if (e === 'body' && dragging.t0 != null) {
          const dt = cursor.t - dragging.t0, dm = cursor.mcap - dragging.m0
          t1 += dt; t2 += dt; m1 += dm; m2 += dm
        }
      }
      const xa = xOfTime(t1), ya = mcapToY(m1), xb = xOfTime(t2), yb = mcapToY(m2)
      const rx = Math.min(xa, xb), ry = Math.min(ya, yb), rw = Math.abs(xb - xa), rh = Math.abs(yb - ya)
      const hov = hoveredId === tl.id || (dragging && dragging.id === tl.id)
      const col = tl.color || '#4fc3f7'
      ctx.save()
      const fo = Number.isFinite(tl.fillOpacity) ? tl.fillOpacity : 0.15
      if (fo > 0) {
        ctx.globalAlpha = Math.max(0, Math.min(1, fo))
        ctx.fillStyle = tl.fill || col
        ctx.fillRect(rx, ry, rw, rh)
        ctx.globalAlpha = 1
      }
      const bw = tl.weight || 1
      if (bw > 0) {
        ctx.strokeStyle = col
        ctx.lineWidth = bw + (hov ? 1 : 0)
        const dk = dashKind(tl.style)
        if (dk === 'dashed') ctx.setLineDash([6, 4])
        else if (dk === 'dotted') ctx.setLineDash([2, 3])
        else ctx.setLineDash([])
        ctx.strokeRect(rx, ry, rw, rh)
        ctx.setLineDash([])
      }
      if (hov) {
        ctx.fillStyle = col
        const p = phone ? PHONE_HANDLE_HALF : PIP + 1
        for (const [hx, hy] of [[xa, ya], [xb, yb], [xa, yb], [xb, ya]]) ctx.fillRect(hx - p, hy - p, p * 2, p * 2)
      }
      if (tl.label) {
        ctx.fillStyle = col
        ctx.font = '500 11px "IBM Plex Sans KR", sans-serif'
        ctx.fillText(tl.label, rx + 4, ry + 12)
      }
      ctx.restore()
      segs.push({ id: tl.id, rect: true, x1: xa, y1: ya, x2: xb, y2: yb })
      continue
    }
    // A dragged endpoint (or mid control) follows the cursor before it is
    // committed, so the line moves under the hand instead of snapping on release.
    let t1 = tl.t1, m1 = tl.m1, t2 = tl.t2, m2 = tl.m2
    let midT = tl.mid?.t, midM = tl.mid?.mcap
    let bend = Number.isFinite(tl.bend) ? tl.bend : null
    let along = Number.isFinite(tl.bendAlong) ? tl.bendAlong : 0
    const dragM = !!(dragging && dragging.id === tl.id && dragging.end === 'm' && cursor && cursor.x != null)
    if (dragging && dragging.id === tl.id && cursor && cursor.t != null) {
      if (dragging.end === 'a') { t1 = cursor.t; m1 = cursor.mcap }
      else if (dragging.end === 'b') { t2 = cursor.t; m2 = cursor.mcap }
    }
    const x1 = xOfTime(t1), y1 = mcapToY(m1)
    const x2 = xOfTime(t2), y2 = mcapToY(m2)
    // Mid pip drag sets the BEND (perpendicular offset only), same formula
    // the release commits, so the curve doesn't jump on lift.
    // The node moves freely: off the line (bend) AND along it (bendAlong).
    if (dragM) {
      bend = bendFromPoint(x1, y1, x2, y2, cursor.x, cursor.y)
      along = alongFromPoint(x1, y1, x2, y2, cursor.x, cursor.y)
    }
    // Bend works on every style, including ray. Ray is a FLAG (tl.ray or
    // legacy style==='ray'), not exclusive of solid/dashed/dotted.
    const ray = isRayLine(tl)
    let bent = false, mx, my
    if (bend != null) {
      // Curved on. bend 0 is a straight curve that still shows its mid pip.
      const mp = midFromBend(x1, y1, x2, y2, bend, along)
      bent = true; mx = mp.x; my = mp.y
    } else if (midT != null && midM != null) {
      // Legacy data-space mid.
      bent = true; mx = xOfTime(midT); my = mcapToY(midM)
    }
    const hov = hoveredId === tl.id || (dragging && dragging.id === tl.id)
    const plotH = geom.plotHeight || geom.height || 1e6

    ctx.save()
    ctx.strokeStyle = tl.color || '#ffaa00'
    ctx.lineWidth = (tl.weight || 1) + (hov ? 1.5 : 0)
    if (hov) { ctx.shadowColor = tl.color || '#ffaa00'; ctx.shadowBlur = 8 }
    const dk = dashKind(tl.style)
    if (dk === 'dashed') ctx.setLineDash([6, 4])
    else if (dk === 'dotted') ctx.setLineDash([2, 3])
    else ctx.setLineDash([])
    ctx.beginPath()
    let rx2 = x2, ry2 = y2  // ray tip (plot edge) when extended
    if (bent) {
      // mid ON stroke; C off-curve so the quadratic passes through mid at t=0.5.
      const { cx, cy } = quadControlFromMid(x1, y1, mx, my, x2, y2)
      ctx.moveTo(x1, y1)
      ctx.quadraticCurveTo(cx, cy, x2, y2)
      if (ray) {
        // Extend past the end along the quadratic tangent at t=1: 2*(P2-C).
        const dx = x2 - cx, dy = y2 - cy
        const tip = rayOutToEdge(x2, y2, dx, dy, geom.plotWidth, plotH)
        ctx.lineTo(tip.x, tip.y)
        rx2 = tip.x; ry2 = tip.y
      }
    } else if (ray && x2 !== x1) {
      // Straight ray: anchor + direction handle, run to the plot edge.
      const { ax, ay, bx, by } = rayEnds(x1, y1, x2, y2)
      const slope = (by - ay) / (bx - ax)
      ctx.moveTo(ax, ay)
      ctx.lineTo(geom.plotWidth, ay + slope * (geom.plotWidth - ax))
      rx2 = geom.plotWidth
      ry2 = ay + slope * (geom.plotWidth - ax)
    } else {
      ctx.moveTo(x1, y1)
      ctx.lineTo(x2, y2)
    }
    ctx.stroke()
    ctx.setLineDash([])
    // Arrowhead at the END point (trenddraw-20261031). Direction = tangent at
    // the end: the quadratic's 2*(P2-C) when curved, else the chord.
    if (tl.arrow) {
      let adx = x2 - x1, ady = y2 - y1
      if (bent) { const q = quadControlFromMid(x1, y1, mx, my, x2, y2); adx = x2 - q.cx; ady = y2 - q.cy }
      const alen = Math.hypot(adx, ady)
      if (alen > 0.5) {
        const ux = adx / alen, uy = ady / alen
        const w = ctx.lineWidth
        const L = 7 + w * 2.5, H = 3.5 + w * 1.3
        const bx0 = x2 - ux * L, by0 = y2 - uy * L
        ctx.beginPath()
        ctx.moveTo(x2 + ux * w * 0.5, y2 + uy * w * 0.5)
        ctx.lineTo(bx0 - uy * H, by0 + ux * H)
        ctx.lineTo(bx0 + uy * H, by0 - ux * H)
        ctx.closePath()
        ctx.fillStyle = tl.color || '#ffaa00'
        ctx.fill()
      }
    }
    // Endpoint squares. Desktop: always on, slightly larger while hovered.
    // Phone: the same square, doubled, and only while this line is
    // highlighted (or being dragged). No second shape.
    // Nodes match rectangle handles exactly (nodes-20261031): only while
    // hovered/selected/dragged, same square size, same colour.
    if (hov) {
      ctx.fillStyle = tl.color || '#ffaa00'
      const p = phone ? PHONE_HANDLE_HALF : PIP + 1
      const pts = [[x1, y1], [x2, y2]]
      if (bent) pts.push([mx, my])
      for (const [hx, hy] of pts) ctx.fillRect(hx - p, hy - p, p * 2, p * 2)
    }
    if (tl.label) {
      ctx.font = '500 11px "IBM Plex Sans KR", sans-serif'
      ctx.fillText(tl.label, x2 + 6, y2 - 4)
    }
    ctx.restore()
    // style rides along so hit-testing can match the DRAWN extent (a ray is
    // grabbable all the way to the plot edge, a segment only between pips).
    // mx/my = on-curve mid (pip + hit); draw converts to control via quadControlFromMid.
    const seg = { id: tl.id, x1, y1, x2, y2, style: tl.style, ray }
    if (bent) { seg.mx = mx; seg.my = my }
    if (ray) { seg.rx2 = rx2; seg.ry2 = ry2 }
    segs.push(seg)
  }
  return segs
}

/**
 * All trendline interaction state. `lines` / `setLines` come from the
 * caller (a scoped setting), everything else is owned here.
 *
 * Flow, matching what every charting tool does:
 *   arm() → first chart click anchors the start → the end follows the
 *   cursor → second click commits. Escape aborts.
 * Existing lines: hover highlights, right-click opens the settings
 * toolbar, and either endpoint can be grabbed and dragged.
 * Curved (toolbar toggle) adds a node; dragging the node is the only arc
 * control. It moves off the line (`bend`) and along it (`bendAlong`).
 * Right-click / phone double-tap always open settings.
 */
export function useTrendlines(token, opts = {}) {
  // opts.getRect  → the canvas DOMRect, for client→canvas conversion
  // opts.redraw   → force a repaint
  //
  // Lines are owned here and keyed on the INSTRUMENT, so they appear only on
  // the chart they were drawn against and vanish when you switch away.
  const [lines, setLinesState] = useState(() => loadDrawings('trend', token))
  const tokenRef = useRef(token)
  const linesRef = useRef([])
  useEffect(() => {
    tokenRef.current = token
    setLinesState(loadDrawings('trend', token))
  }, [token])
  useEffect(() => { pruneLegacyDrawings() }, [])
  // (chart2-bounce) Both chart slots can chart the same token. Each holds its
  // own copy of the token's trendlines, so a save on one overwrote the other's
  // in storage and the other never showed the change. A save now tells the
  // other slot, which reloads from storage (skipped mid-drag on that chart).
  const syncSrcRef = useRef(null)
  if (syncSrcRef.current === null) syncSrcRef.current = {}
  const setLines = useCallback((v) => {
    setLinesState(prev => {
      const nv = typeof v === 'function' ? v(prev) : v
      // Keep linesRef in lockstep with the commit. On pointerup we clear
      // dragRef and redraw immediately — if linesRef still held the pre-drag
      // mid, the curve flashed back to its start for one frame before React
      // flushed state. Endpoints had the same race; the arc made it obvious.
      linesRef.current = Array.isArray(nv) ? nv : []
      const tok = tokenRef.current
      saveDrawings('trend', tok, nv)
      // Deferred: this runs inside a state updater, and the listener sets
      // state on another component.
      const src = syncSrcRef.current
      queueMicrotask(() => {
        try { window.dispatchEvent(new CustomEvent('cheddar-drawings', { detail: { kind: 'trend', token: tok, src } })) } catch {}
      })
      return nv
    })
  }, [])
  const [mode, setMode] = useState(null)          // null | 'armed' | 'drawing' | 'phonePlacing'
  const shapeRef = useRef('line')                 // what the armed tool draws: 'line' | 'rect'
  const [pending, setPending] = useState(null)    // {t, mcap} start anchor
  const [menu, setMenu] = useState({ show: false, x: 0, y: 0, line: null })
  const modeRef = useRef(null)
  const pendingRef = useRef(null)
  const cursorPxRef = useRef({ x: -1, y: -1 })
  const cursorDataRef = useRef(null)
  const screenRef = useRef([])
  const hoveredRef = useRef(null)
  const dragRef = useRef(null)                    // { id, end: 'a' | 'b' | 'm' }
  const phonePressRef = useRef(null)              // long-press -> settings, or slop -> endpoint drag
  // Phone placement. toDataRef is the draw pass's px -> {t, mcap} converter
  // (same math as the rubber-band cursor, so no extra snapping), and
  // menuPointRef is the double-tap that opened the context menu, already in
  // data space so a scroll between the tap and the menu pick can't move it.
  const toDataRef = useRef(null)
  const menuPointRef = useRef(null)               // {t, mcap, x, y} | null
  const placeRef = useRef(null)                   // live finger listeners while placing
  // phonelag-20261102: drag listeners were never tracked, so an unmount or a
  // second beginDrag before lift (multi-touch) left window listeners behind.
  const dragLsRef = useRef(null)
  const dropDragListeners = () => {
    const d = dragLsRef.current
    if (!d) return
    window.removeEventListener('pointermove', d.onMove)
    window.removeEventListener('pointerup', d.onUp)
    window.removeEventListener('pointercancel', d.onUp)
    dragLsRef.current = null
  }
  useEffect(() => () => {
    dropPlaceListeners()
    dropDragListeners()
    const p = phonePressRef.current
    if (!p) return
    clearTimeout(p.timer)
    window.removeEventListener('pointermove', p.onMove)
    window.removeEventListener('pointerup', p.onUp)
    window.removeEventListener('pointercancel', p.onUp)
  }, [])

  useEffect(() => { linesRef.current = Array.isArray(lines) ? lines : [] }, [lines])
  useEffect(() => {
    const onSync = (e) => {
      const d = e && e.detail
      if (!d || d.kind !== 'trend' || d.src === syncSrcRef.current) return
      if (!d.token || d.token !== tokenRef.current) return
      if (dragRef.current) return   // this chart's own drag owns its lines
      const next = loadDrawings('trend', d.token)
      linesRef.current = next
      setLinesState(next)
      opts.redraw?.()
    }
    window.addEventListener('cheddar-drawings', onSync)
    return () => window.removeEventListener('cheddar-drawings', onSync)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => { modeRef.current = mode }, [mode])
  useEffect(() => { pendingRef.current = pending }, [pending])

  // New drawing from the placement anchors. Rectangles share the store with
  // trendlines (shape:'rect'): same persistence, sync, menu, delete, Grok.
  // (srline-trend-20261019) The last menu choice is the next drawing's default.
  const newShape = (a, pt) => (shapeRef.current === 'rect' || a?.shape === 'rect')
    ? { id: `tl_${Date.now()}`, shape: 'rect', t1: a.t, m1: a.mcap, t2: pt.t, m2: pt.mcap,
        color: '#4fc3f7', fill: '#4fc3f7', fillOpacity: 0.15, weight: 1, style: 'solid', ...loadDrawDefaults('rect'), label: '' }
    : { id: `tl_${Date.now()}`, t1: a.t, m1: a.mcap, t2: pt.t, m2: pt.mcap,
        color: '#ffaa00', weight: 1, style: 'solid', ...loadDrawDefaults('trend'), label: '' }

  const dropPlaceListeners = () => {
    const pl = placeRef.current
    if (!pl) return
    window.removeEventListener('pointermove', pl.onMove)
    window.removeEventListener('pointerup', pl.onUp)
    window.removeEventListener('pointercancel', pl.onCancel)
    placeRef.current = null
  }
  const cancel = useCallback(() => {
    dropPlaceListeners()
    shapeRef.current = 'line'
    modeRef.current = null
    pendingRef.current = null
    setMode(null); setPending(null)
    opts.redraw?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const arm = useCallback((shape) => { shapeRef.current = shape === 'rect' ? 'rect' : 'line'; setPending(null); setMode('armed') }, [])
  const armRect = useCallback(() => arm('rect'), [arm])

  /** Phone: remember where the double-tap that opened the menu landed. */
  const notePhoneMenuPoint = useCallback((clientX, clientY) => {
    const r = opts.getRect?.()
    const conv = toDataRef.current
    menuPointRef.current = null
    if (!r || !conv) return
    const x = clientX - r.left, y = clientY - r.top
    const pt = conv(x, y)
    if (pt && pt.t > 0 && pt.mcap > 0) menuPointRef.current = { t: pt.t, mcap: pt.mcap, x, y }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Phone 'Trendline' pick: point 1 = the double-tap, then place point 2.
   *  Falls back to the desktop two-click arm if no tap was recorded. */
  const armPhone = useCallback((shape) => {
    // trenddraw-20261031: press = point 1, swipe previews, release = point 2.
    const sh = shape === 'rect' ? 'rect' : 'line'
    menuPointRef.current = null
    dropPlaceListeners()
    shapeRef.current = sh
    pendingRef.current = null
    modeRef.current = 'phonePlacing'
    setPending(null)
    setMode('phonePlacing')
    cursorPxRef.current = { x: -1, y: -1 }
    hoveredRef.current = null
    opts.redraw?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Phone back button aborts placement: push a same-URL history entry while
  // placing; popping it cancels. Finishing / the X button pops our entry off.
  useEffect(() => {
    if (mode !== 'phonePlacing') return
    let popped = false
    try { window.history.pushState({ ...(window.history.state || {}), cheddarTrendPlace: true }, '', window.location.href) } catch {}
    const onPop = () => { popped = true; cancel() }
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      if (!popped) { try { if (window.history.state?.cheddarTrendPlace) window.history.back() } catch {} }
    }
  }, [mode, cancel])

  useEffect(() => {
    if (!mode) return
    const onKey = (e) => { if (e.key === 'Escape') cancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, cancel])

  /** Called from the draw pass: stash the data-space cursor + segments. */
  const syncFrame = useCallback((cursorData, segments, toData) => {
    cursorDataRef.current = cursorData
    screenRef.current = segments
    if (toData) toDataRef.current = toData
  }, [])

  /** Mouse move over the plot. Returns true if a repaint is needed. */
  const handleMove = useCallback((mx, my) => {
    cursorPxRef.current = { x: mx, y: my }
    if (dragRef.current || modeRef.current === 'drawing') return true

    const segs = screenRef.current
    let hit = null
    if (segs.length && !modeRef.current) {
      let best = HOVER_PX
      for (const sg of segs) {
        let d
        if (sg.rect) {
          d = distToRectBorder(mx, my, sg.x1, sg.y1, sg.x2, sg.y2)
        } else if (sg.mx != null && sg.my != null) {
          d = distToBent(mx, my, sg.x1, sg.y1, sg.mx, sg.my, sg.x2, sg.y2)
          if (sg.ray && sg.rx2 != null) {
            d = Math.min(d, distToSegment(mx, my, sg.x2, sg.y2, sg.rx2, sg.ry2))
          }
        } else if (sg.ray || sg.style === 'ray') {
          // Straight ray — same normalisation the draw pass uses.
          const { ax, ay, bx, by } = rayEnds(sg.x1, sg.y1, sg.x2, sg.y2)
          d = distToRay(mx, my, ax, ay, bx, by)
        } else {
          d = distToSegment(mx, my, sg.x1, sg.y1, sg.x2, sg.y2)
        }
        if (d < best) { best = d; hit = sg.id }
      }
    }
    if (hit !== hoveredRef.current) { hoveredRef.current = hit; return true }
    return false
  }, [])

  /** Which endpoint / mid pip (if any) is under the cursor. */
  const endpointAt = useCallback((mx, my, radius = GRAB_PX) => {
    // Nearest pip wins, not first-listed. With a ray the two pips can sit
    // close together while the line runs off to the edge, and first-match
    // meant the anchor could be un-grabbable whenever the direction handle
    // was tested first. For a ray 'a' is the START (moves the whole ray) and
    // 'b' is the DIRECTION handle (sets the angle it leaves at).
    // Mid ('m') competes on the same nearest-wins basis with the ends.
    let best = radius, hit = null
    for (const sg of screenRef.current) {
      const da = Math.hypot(mx - sg.x1, my - sg.y1)
      if (da <= best) { best = da; hit = { id: sg.id, end: 'a' } }
      const db = Math.hypot(mx - sg.x2, my - sg.y2)
      if (db <= best) { best = db; hit = { id: sg.id, end: 'b' } }
      if (sg.rect) {
        const dc = Math.hypot(mx - sg.x1, my - sg.y2)
        if (dc <= best) { best = dc; hit = { id: sg.id, end: 'c' } }
        const dd = Math.hypot(mx - sg.x2, my - sg.y1)
        if (dd <= best) { best = dd; hit = { id: sg.id, end: 'd' } }
      }
      if (sg.mx != null && sg.my != null) {
        const dm = Math.hypot(mx - sg.mx, my - sg.my)
        if (dm <= best) { best = dm; hit = { id: sg.id, end: 'm' } }
      }
    }
    return hit
  }, [])

  /** Nearest line body under (mx,my), or null. */
  const lineAt = useCallback((mx, my, radius = HOVER_PX) => {
    const segs = screenRef.current
    let best = radius, hit = null
    for (const sg of segs) {
      let d
      if (sg.rect) {
        d = distToRectBorder(mx, my, sg.x1, sg.y1, sg.x2, sg.y2)
      } else if (sg.mx != null && sg.my != null) {
        d = distToBent(mx, my, sg.x1, sg.y1, sg.mx, sg.my, sg.x2, sg.y2)
        if (sg.ray && sg.rx2 != null) {
          d = Math.min(d, distToSegment(mx, my, sg.x2, sg.y2, sg.rx2, sg.ry2))
        }
      } else if (sg.ray || sg.style === 'ray') {
        const { ax, ay, bx, by } = rayEnds(sg.x1, sg.y1, sg.x2, sg.y2)
        d = distToRay(mx, my, ax, ay, bx, by)
      } else {
        d = distToSegment(mx, my, sg.x1, sg.y1, sg.x2, sg.y2)
      }
      if (d < best) { best = d; hit = sg.id }
    }
    return hit
  }, [])

  /**
   * Left-click on the plot. Returns true if trendlines consumed it, so the
   * caller can skip pan / alert arming.
   */
  const handlePointerDown = useCallback((mx, my, pointerType = 'mouse', clientX = 0, clientY = 0) => {
    const beginDrag = (grab) => {
      dropDragListeners()
      dragRef.current = grab
      if (hoveredRef.current !== grab.id) {
        hoveredRef.current = grab.id
        opts.redraw?.()
      }
      // WINDOW-level pointer listeners for the drag, deliberately.
      // The caller must preventDefault() on pointerdown to stop the chart
      // panning, and preventDefault on a POINTERdown suppresses the
      // browser's compatibility MOUSE events, which is what the hover path
      // (onMouseMove) rides on. So the cursor position simply stopped
      // updating the moment you grabbed a pip and the endpoint never moved.
      // Tracking on pointermove at the window sidesteps that entirely, and
      // also keeps the drag alive when the cursor leaves the canvas.
      const onMove = (ev) => {
        const r = opts.getRect?.()
        if (!r) return
        cursorPxRef.current = { x: ev.clientX - r.left, y: ev.clientY - r.top }
        opts.redraw?.()
      }
      const onUp = () => {
        dropDragListeners()
        const drag = dragRef.current
        const pt = cursorDataRef.current
        // Commit BEFORE clearing dragRef so a redraw never sees
        // "no drag + stale mid" (the release flash back to start).
        if (drag && drag.end === 'm') {
          // Mid pip -> bend, measured against the chord as drawn.
          const sg = screenRef.current.find(x => x.id === drag.id)
          const c = cursorPxRef.current
          if (sg && c && c.x >= 0) {
            const b = Math.round(bendFromPoint(sg.x1, sg.y1, sg.x2, sg.y2, c.x, c.y) * 100) / 100
            const al = Math.round(alongFromPoint(sg.x1, sg.y1, sg.x2, sg.y2, c.x, c.y) * 100) / 100
            setLines(prev => (prev || []).map(t => {
              if (t.id !== drag.id) return t
              const { mid, ...rest } = t   // drop the legacy data-space mid
              return { ...rest, bend: b, bendAlong: al }
            }))
          }
        } else if (drag && pt && pt.t != null) {
          setLines(prev => (prev || []).map(t => {
            if (t.id !== drag.id) return t
            if (drag.end === 'a') return { ...t, t1: pt.t, m1: pt.mcap }
            if (drag.end === 'b') return { ...t, t2: pt.t, m2: pt.mcap }
            if (drag.end === 'c') return { ...t, t1: pt.t, m2: pt.mcap }
            if (drag.end === 'd') return { ...t, t2: pt.t, m1: pt.mcap }
            if (drag.end === 'body' && drag.t0 != null) {
              const dt = pt.t - drag.t0, dm = pt.mcap - drag.m0
              const n = { ...t, t1: t.t1 + dt, t2: t.t2 + dt, m1: t.m1 + dm, m2: t.m2 + dm }
              return (n.m1 > 0 && n.m2 > 0) ? n : t
            }
            return t
          }))
        }
        dragRef.current = null
        opts.redraw?.()
      }
      dragLsRef.current = { onMove, onUp }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    }

    const openPhoneMenu = (id, x, y) => {
      const line = linesRef.current.find(tl => tl.id === id)
      if (!line) return
      setMenu({
        show: true,
        x: x > window.innerWidth - 340 ? x - 330 : x + 8,
        y: Math.max(6, Math.min(y - 12, window.innerHeight - 180)),
        line,
      })
    }

    // Phone placement of point 2: the finger drives the temporary end. A
    // tap or a swipe both commit where the finger LIFTS (a still tap lifts
    // where it landed). Consumes the gesture so the chart doesn't pan.
    // trenddraw-20261031: press-drag-release placement, desktop and phone.
    // Down anchors point 1, the drag previews, the lift commits point 2. A
    // near-zero drag commits nothing and leaves the tool armed. The caller
    // treats `true` as "owned", so the chart does not pan meanwhile.
    const startPlace = (restMode) => {
      dropPlaceListeners()
      const conv = toDataRef.current
      const p0 = conv ? conv(mx, my) : cursorDataRef.current
      if (!p0 || !(p0.t > 0) || !(p0.mcap > 0)) return false
      const start = { t: p0.t, mcap: p0.mcap, shape: shapeRef.current }
      const live = restMode === 'phonePlacing' ? 'phonePlacing' : 'drawing'
      pendingRef.current = start
      modeRef.current = live
      setPending(start)
      setMode(live)
      cursorPxRef.current = { x: mx, y: my }
      hoveredRef.current = null
      opts.redraw?.()
      const toPx = (ev) => {
        const r = opts.getRect?.()
        return r ? { x: ev.clientX - r.left, y: ev.clientY - r.top } : null
      }
      const minPx = pointerType === 'touch' ? 8 : 4
      const backToArmed = () => {
        pendingRef.current = null
        modeRef.current = restMode
        setPending(null)
        setMode(restMode)
        opts.redraw?.()
      }
      const pl = {}
      pl.onMove = (ev) => {
        const px = toPx(ev)
        if (!px) return
        cursorPxRef.current = px
        if (ev.cancelable && pointerType === 'touch') ev.preventDefault()
        opts.redraw?.()
      }
      pl.onUp = (ev) => {
        dropPlaceListeners()
        const px = toPx(ev) || cursorPxRef.current
        if (!px || Math.hypot(px.x - mx, px.y - my) < minPx) { backToArmed(); return }
        const a = pendingRef.current
        const c2 = toDataRef.current
        const pt = c2 ? c2(px.x, px.y) : cursorDataRef.current
        if (a && pt && pt.t > 0 && pt.mcap > 0) {
          setLines(prev => [...(Array.isArray(prev) ? prev : []), newShape(a, pt)])
        }
        cancel()
      }
      // A system-cancelled gesture commits nothing; the tool stays armed.
      pl.onCancel = () => { dropPlaceListeners(); backToArmed() }
      placeRef.current = pl
      window.addEventListener('pointermove', pl.onMove, { passive: false })
      window.addEventListener('pointerup', pl.onUp)
      window.addEventListener('pointercancel', pl.onCancel)
      return true
    }

    if (modeRef.current === 'phonePlacing') {
      startPlace('phonePlacing')
      return true
    }

    // Phone: touch anywhere on the line highlights it and shows the squares.
    // A still hold opens this line's menu. Moving on a square drags that
    // endpoint. The body does not drag the whole line.
    if (!modeRef.current && pointerType === 'touch') {
      const prev = phonePressRef.current
      if (prev) {
        clearTimeout(prev.timer)
        window.removeEventListener('pointermove', prev.onMove)
        window.removeEventListener('pointerup', prev.onUp)
        window.removeEventListener('pointercancel', prev.onUp)
        phonePressRef.current = null
      }
      const hit = lineAt(mx, my, HOVER_TOUCH_PX)
      if (!hit) {
        if (hoveredRef.current) {
          hoveredRef.current = null
          opts.redraw?.()
        }
        return false
      }
      hoveredRef.current = hit
      opts.redraw?.()
      const grab = endpointAt(mx, my, PHONE_HANDLE_HIT)
      const press = {
        id: hit,
        x: clientX,
        y: clientY,
        moved: false,
        grab: grab && grab.id === hit ? grab : null,
        rectBody: !!screenRef.current.find(x => x.id === hit && x.rect),
        timer: 0,
        onMove: null,
        onUp: null,
      }
      const dropPress = () => {
        clearTimeout(press.timer)
        window.removeEventListener('pointermove', press.onMove)
        window.removeEventListener('pointerup', press.onUp)
        window.removeEventListener('pointercancel', press.onUp)
        if (phonePressRef.current === press) phonePressRef.current = null
      }
      press.onMove = (ev) => {
        const dx = ev.clientX - press.x
        const dy = ev.clientY - press.y
        if (dx * dx + dy * dy < PHONE_SLOP * PHONE_SLOP) return
        press.moved = true
        dropPress()
        const r = opts.getRect?.()
        if (!press.grab && press.rectBody && r && toDataRef.current) {
          const p0 = toDataRef.current(press.x - r.left, press.y - r.top)
          if (p0 && p0.t > 0) {
            cursorPxRef.current = { x: ev.clientX - r.left, y: ev.clientY - r.top }
            beginDrag({ id: press.id, end: 'body', t0: p0.t, m0: p0.mcap })
          }
          return
        }
        if (!press.grab) return
        if (r) cursorPxRef.current = { x: ev.clientX - r.left, y: ev.clientY - r.top }
        beginDrag(press.grab)
      }
      press.onUp = () => { dropPress() }
      press.timer = setTimeout(() => {
        if (phonePressRef.current !== press || press.moved) return
        dropPress()
        openPhoneMenu(press.id, press.x, press.y)
        opts.redraw?.()
      }, PHONE_LONG_MS)
      phonePressRef.current = press
      window.addEventListener('pointermove', press.onMove)
      window.addEventListener('pointerup', press.onUp)
      window.addEventListener('pointercancel', press.onUp)
      return true
    }

    // Mouse: grabbing an endpoint takes priority over everything.
    if (!modeRef.current) {
      const grab = endpointAt(mx, my, GRAB_PX)
      if (!grab) {
        const hit = lineAt(mx, my, HOVER_PX)
        // Rectangle border: grab moves the whole box.
        const sgR = hit && screenRef.current.find(x => x.id === hit && x.rect)
        const ptR = cursorDataRef.current
        if (sgR && ptR && ptR.t != null) {
          beginDrag({ id: hit, end: 'body', t0: ptR.t, m0: ptR.mcap })
          return true
        }
        if (hit !== hoveredRef.current) {
          hoveredRef.current = hit
          opts.redraw?.()
        }
        return !!hit
      }
      beginDrag(grab)
      return true
    }
    // Armed (or a stale 'drawing' whose drag was lost): this press is point 1.
    startPlace('armed')
    return true
  }, [cancel, endpointAt, lineAt, setLines])

  /** Kept for the caller's onPointerUp; the window listener above owns the
   *  commit, so this only reports whether a drag was in flight. */
  const handlePointerUp = useCallback(() => !!dragRef.current, [])

  /** Right-click / phone double-tap: open this line's settings. (It used to
   *  drop an arc mid on an unbent body; arc now lives in the toolbar.)
   *  Phone has no hover, so we hit-test from the event coords when
   *  hoveredRef is empty (otherwise double-tap always missed the toolbar). */
  const handleContextMenu = useCallback((e) => {
    const r = opts.getRect?.()
    const cx = r ? e.clientX - r.left : null
    const cy = r ? e.clientY - r.top : null
    let id = hoveredRef.current
    if (!id && cx != null) {
      id = lineAt(cx, cy, HOVER_TOUCH_PX)
      if (id) hoveredRef.current = id
    }
    if (!id) return false
    const line = linesRef.current.find(t => t.id === id)
    if (!line) return false
    e.preventDefault()
    opts.redraw?.()

    setMenu({
      show: true,
      x: e.clientX > window.innerWidth - 340 ? e.clientX - 330 : e.clientX + 8,
      y: Math.max(6, Math.min(e.clientY - 12, window.innerHeight - 180)),
      line,
    })
    return true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineAt])

  // Menu edits (the only caller) also become the next drawing's defaults.
  const update = useCallback((u) => {
    saveDrawDefaults(u)
    setLines(prev => (prev || []).map(t => t.id === u.id ? { ...t, ...u } : t))
  }, [setLines])
  const remove = useCallback((id) => setLines(prev => (prev || []).filter(t => t.id !== id)), [setLines])
  // Programmatic add (Grok drawing bridge). Returns the new id.
  const add = useCallback((ln) => {
    const id = `tl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
    setLines(prev => [...(Array.isArray(prev) ? prev : []), { color: '#ffaa00', weight: 1, style: 'solid', label: '', ...ln, id }])
    return id
  }, [setLines])

  return {
    lines,
    mode, pending, menu, setMenu,
    arm, armRect, armPhone, notePhoneMenuPoint, cancel, shapeRef, syncFrame, handleMove, handlePointerDown, handlePointerUp, handleContextMenu,
    endpointAt,
    update, remove, add,
    linesRef, cursorPxRef, cursorDataRef, hoveredRef, dragRef, pendingRef, modeRef,
  }
}

// Remembered menu settings for new trendlines / rectangles. Only style keys:
// label, anchors and the curve node stay per-drawing.
const DRAW_DEFAULT_KEYS = { trend: ['color', 'weight', 'style', 'ray', 'arrow'], rect: ['color', 'weight', 'style', 'fill', 'fillOpacity'] }
const drawDefaultsKey = (kind) => `solcheddar_${kind}_draw_defaults`
function loadDrawDefaults(kind) {
  try {
    const v = JSON.parse(localStorage.getItem(drawDefaultsKey(kind)) || 'null')
    if (!v || typeof v !== 'object') return {}
    const out = {}
    for (const k of DRAW_DEFAULT_KEYS[kind]) if (v[k] != null) out[k] = v[k]
    return out
  } catch { return {} }
}
function saveDrawDefaults(line) {
  if (!line) return
  const kind = line.shape === 'rect' ? 'rect' : 'trend'
  const out = {}
  for (const k of DRAW_DEFAULT_KEYS[kind]) if (line[k] != null) out[k] = line[k]
  if (kind === 'trend') out.ray = !!(line.ray || line.style === 'ray')
  try { localStorage.setItem(drawDefaultsKey(kind), JSON.stringify(out)) } catch {}
}

const LINE_WEIGHTS = [1, 2, 3, 4]
const LINE_STYLES = [
  { value: 'solid',  label: 'Solid',  dash: '——' },
  { value: 'dashed', label: 'Dashed', dash: '- -' },
  { value: 'dotted', label: 'Dotted', dash: '···' },
  { value: 'ray',    label: 'Ray',    dash: ' →' },
]

export default function SRLineSettingsMenu({ show, x, y, line, onUpdate, onDelete, onClose, onAlert }) {
  // Frame, fit (8px clamp, flips, max-height + internal scroll, re-fit on
  // content / window resize), outside-click and Esc: menu/ChartMenu. Both
  // trendlines and S/R lines use this menu.
  const [color, setColor]   = useState(line?.color   || '#ff4d4d')
  const [weight, setWeight] = useState(line?.weight  || 1)
  const [style, setStyle]   = useState(() => (line?.style === 'ray' ? 'solid' : (line?.style || 'dashed')))
  const [ray, setRay]       = useState(() => !!(line?.ray || line?.style === 'ray'))
  const [arrow, setArrow]   = useState(() => !!line?.arrow)
  const [label, setLabel]   = useState(line?.label   || '')
  const [mid, setMid]       = useState(line?.mid     || null)
  const [bend, setBend]     = useState(Number.isFinite(line?.bend) ? line.bend : null)
  const [fill, setFill]     = useState(line?.fill || line?.color || '#4fc3f7')
  const [fillOpacity, setFillOpacity] = useState(Number.isFinite(line?.fillOpacity) ? line.fillOpacity : 0.15)
  const isRect = line?.shape === 'rect'
  useEffect(() => {
    if (show && line) {
      setColor(line.color)
      setWeight(line.weight)
      setStyle(line.style === 'ray' ? 'solid' : (line.style || 'dashed'))
      setRay(!!(line.ray || line.style === 'ray'))
      setArrow(!!line.arrow)
      setLabel(line.label || '')
      setMid(line.mid || null)
      setBend(Number.isFinite(line.bend) ? line.bend : null)
      setFill(line.fill || line.color || '#4fc3f7')
      setFillOpacity(Number.isFinite(line.fillOpacity) ? line.fillOpacity : 0.15)
    }
  }, [show, line])


  // AUTO-APPLY. Every control calls this with just what it changed; the rest
  // of the draft is merged in, so local state and the committed line move
  // together and there is no staged copy to get out of sync.
  // Ray stays a separate flag so dashed/dotted/solid can all extend; legacy
  // style==='ray' migrates to solid + ray here.
  const apply = (patch) => {
    const next = { color, weight, style, ray, arrow, label, mid, bend, fill, fillOpacity, ...patch }
    if ('fill' in patch) setFill(next.fill)
    if ('fillOpacity' in patch) setFillOpacity(next.fillOpacity)
    if ('color'  in patch) setColor(next.color)
    if ('weight' in patch) setWeight(next.weight)
    if ('style'  in patch) setStyle(next.style)
    if ('ray'    in patch) setRay(next.ray)
    if ('arrow'  in patch) setArrow(next.arrow)
    if ('label'  in patch) setLabel(next.label)
    if ('mid'    in patch) setMid(next.mid)
    if ('bend'   in patch) setBend(next.bend)
    // Spread the DRAFT over `line`, never the reverse: the parent updates
    // srLinesRef without refreshing this prop, so `line` is the object we
    // opened with and stays that way until the menu closes.
    onUpdate({
      ...line,
      color: next.color,
      weight: next.weight,
      style: next.style === 'ray' ? 'solid' : next.style,
      ray: !!(next.ray || next.style === 'ray'),
      label: next.label,
      mid: next.mid,
      // Arc only exists on trendlines (t1/t2); S/R lines never get the key.
      ...(Number.isFinite(line.t1) && line.shape !== 'rect' ? { arrow: !!next.arrow } : {}),
      ...(Number.isFinite(line.t1) && line.shape !== 'rect' ? { bend: Number.isFinite(next.bend) ? next.bend : null } : {}),
      // Toggling Curved on/off puts the node back in the middle.
      ...(Number.isFinite(line.t1) && line.shape !== 'rect' && 'bend' in patch ? { bendAlong: 0 } : {}),
      ...(line.shape === 'rect' ? { ray: false, fill: next.fill, fillOpacity: next.fillOpacity } : {}),
    })
  }
  const curved = bend != null || !!mid

  if (!show || !line) return null

  const currentStyle = LINE_STYLES.find(s => s.value === style)
  const styleOpts = LINE_STYLES.filter(s => s.value !== 'ray').map(s => ({ value: s.value, label: `${s.dash} ${s.label}` }))

  return (
    <ChartMenu anchor={{ x, y }} onClose={onClose}>
      <MenuRow>
        {/* STYLE */}
        <MenuSelect value={style} options={styleOpts}
          renderValue={() => currentStyle?.dash}
          onChange={(v) => apply({ style: v })}>
          {/* Ray stays in this menu as a checkbox — applies to whichever
              stroke (solid/dashed/dotted) is selected, not a 4th style. */}
          {!isRect && <label
            className={`cm-option cm-check-option${ray ? ' selected' : ''}`}
            title="Extend as ray (any stroke style)"
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            <span style={{ flex: 1, textAlign: 'left' }}>→ Ray</span>
            <input
              type="checkbox"
              checked={ray}
              onChange={e => {
                const on = e.target.checked
                apply(style === 'ray' ? { ray: on, style: 'solid' } : { ray: on })
              }}
            />
          </label>}
          {!isRect && Number.isFinite(line.t1) && <label
            className={`cm-option cm-check-option${arrow ? ' selected' : ''}`}
            title="Arrowhead at the end point"
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            <span style={{ flex: 1, textAlign: 'left' }}>{'\u27a4'} Arrow</span>
            <input type="checkbox" checked={arrow} onChange={e => apply({ arrow: e.target.checked })} />
          </label>}
        </MenuSelect>

        <MenuDivider />

        {/* COLOR — shared ColorPicker (rect: border colour) */}
        <MenuSwatch value={color} onChange={c => apply({ color: c })} />

        {isRect && (
          <>
            <MenuDivider />
            {/* FILL colour + opacity (rectangles only) */}
            <MenuSwatch value={fill} onChange={c => apply({ fill: c })} />
            <span className="cm-row-ctl" title="Fill opacity" onMouseDown={e => e.stopPropagation()}>
              <input className="cm-range" style={{ width: 64 }} type="range" min={0} max={100} step={1}
                value={Math.round((fillOpacity ?? 0.15) * 100)}
                onChange={e => apply({ fillOpacity: Number(e.target.value) / 100 })} />
              <span className="cm-range-val">{Math.round((fillOpacity ?? 0.15) * 100)}%</span>
            </span>
          </>
        )}

        <MenuDivider />

        {/* WEIGHT */}
        <MenuWeight value={weight} weights={LINE_WEIGHTS} onChange={(w) => apply({ weight: w })} />

        <MenuDivider />

        {/* LABEL */}
        <MenuText value={label} maxLength={16} placeholder="label…"
          onChange={(v) => apply({ label: v })} />
      </MenuRow>

      <MenuIndex value={line.index} onChange={(v) => { line.index = v; onUpdate({ ...line, index: v }) }} />

      <MenuActions>
        {/* Alert at this line's price. Only for horizontal S/R lines: this
            toolbar is shared with trendlines, which carry two anchors (m1/m2)
            and therefore no single price to alert on — their value depends on
            WHEN you ask. `line.mcap` is present on S/R lines and absent on
            trendlines, so it gates the button without needing a type flag.
            onAlert is optional so the trendline call site just omits it. */}
        {onAlert && Number.isFinite(line?.mcap) && (
          <MenuButton variant="icon" title="Set alert at this line"
            onClick={() => { onAlert(line.mcap); onClose() }}>
            🔔
          </MenuButton>
        )}
        {/* Curved toggle for any trendline (t1/t2), including rays. The node
            drag is the only arc control (no slider). S/R lines have mcap and
            no t1 — the Number.isFinite(t1) gate keeps them out. */}
        {Number.isFinite(line?.t1) && !isRect && (
          <>
            <MenuButton variant="outline" on={curved}
              title={curved ? 'Straighten the line' : 'Curve the line (then drag the node)'}
              onClick={() => apply(curved ? { bend: null, mid: null } : { bend: 0.3, mid: null })}>
              {curved ? '✓ Curved' : 'Curved'}
            </MenuButton>
          </>
        )}
        <MenuButton variant="danger" onClick={() => { onDelete(line.id); onClose() }}>
          DELETE
        </MenuButton>
      </MenuActions>
    </ChartMenu>
  )
}
